// synthetic-arabic: reviewed-no-scripture — the Arabic here is ordinary interface wording invented
// for this application; it contains no Qur'an, hadith, translation or religious quotation.
import { useEffect, useRef, useState } from 'react';
import { messages, useMessages } from '../i18n.js';

const T = messages({
  ar: {
    stop: (reference: string) => `إيقاف تلاوة ${reference}`,
    play: (reference: string) => `تلاوة ${reference}`,
    stopTitle: 'إيقاف التلاوة',
    playTitle: 'تشغيل التلاوة',
  },
  en: {
    stop: (reference: string) => `Stop the recitation of ${reference}`,
    play: (reference: string) => `Recitation of ${reference}`,
    stopTitle: 'Stop the recitation',
    playTitle: 'Play the recitation',
  },
});

/**
 * The small play control beside a woven verse (founder direction 2026-08-25): one tap recites, one
 * tap pauses. The audio element is created on the first tap — a verse appearing on screen is not a
 * request to the provider — and the asset is always the provider's human recitation, never
 * synthesis.
 */
export function RecitationButton({
  src,
  reference,
}: {
  readonly src: string;
  readonly reference: string;
}): React.JSX.Element {
  const t = useMessages(T);
  const [playing, setPlaying] = useState(false);
  const player = useRef<HTMLAudioElement | null>(null);

  useEffect(
    () => () => {
      player.current?.pause();
      player.current = null;
    },
    [],
  );

  const toggle = (): void => {
    if (player.current === null) {
      const audio = new Audio(src);
      audio.addEventListener('ended', () => {
        setPlaying(false);
      });
      audio.addEventListener('error', () => {
        setPlaying(false);
      });
      player.current = audio;
    }
    if (playing) {
      player.current.pause();
      setPlaying(false);
    } else {
      void player.current.play().then(
        () => {
          setPlaying(true);
        },
        () => {
          setPlaying(false);
        },
      );
    }
  };

  return (
    <button
      type="button"
      className="recite-button"
      aria-label={playing ? t.stop(reference) : t.play(reference)}
      title={playing ? t.stopTitle : t.playTitle}
      onClick={toggle}
    >
      {playing ? '⏸' : '▶'}
    </button>
  );
}
